import type { api } from '@udm/contracts';
import type { GeoJSONSource, GeoJSONSourceSpecification, Map as MapLibreMap } from 'maplibre-gl';
import { deviceStatic } from '../../realtime/deviceStatic';
import { deviceLive } from '../../realtime/stores';

export const ASSETS_SOURCE = 'gis-assets';
export const HEAT_SOURCE = 'ops-heat';

type FC = GeoJSONSourceSpecification['data'];

/** GeoJSON of GIS assets (properties: id, name, type, color, status). */
export function assetsData(items: api.Asset[], types: api.AssetType[]): FC {
  const color = new Map(types.map((t) => [t.key, t.color ?? '#6b7280']));
  return {
    type: 'FeatureCollection',
    features: items
      .filter((a) => a.geometry)
      .map((a) => ({
        type: 'Feature',
        id: a.id,
        geometry: a.geometry as never,
        properties: {
          id: a.id,
          name: a.name,
          type: a.type_key,
          color: color.get(a.type_key) ?? '#6b7280',
          status: a.status,
        },
      })),
  } as FC;
}

/**
 * Heat map of one metric from the live store (current values of the devices on the map): weight = value normalized
 * to the [min, max] of the visible values. Rebuilt on demand (metric change, throttled live updates), never per event.
 */
export function heatData(metric: string): { data: FC; min: number; max: number; n: number } {
  const pts: { lon: number; lat: number; v: number }[] = [];
  for (const d of deviceStatic.values()) {
    if (d.lon === undefined || d.lat === undefined) continue;
    const v = deviceLive.get(d.id)?.metrics?.[metric];
    if (typeof v === 'number' && Number.isFinite(v)) pts.push({ lon: d.lon, lat: d.lat, v });
  }
  const vs = pts.map((p) => p.v).sort((a, b) => a - b);
  // robust range: 5th–95th percentile, so one outlier does not flatten the map
  const min = vs.length ? vs[Math.floor(vs.length * 0.05)]! : 0;
  const max = vs.length ? vs[Math.min(vs.length - 1, Math.floor(vs.length * 0.95))]! : 1;
  const span = max - min || 1;
  return {
    data: {
      type: 'FeatureCollection',
      features: pts.map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
        properties: { w: Math.max(0, Math.min(1, (p.v - min) / span)) },
      })),
    } as FC,
    min,
    max,
    n: pts.length,
  };
}

/** Adds the (empty, hidden) asset and heat layers under the device markers. */
export function addOverlayLayers(map: MapLibreMap, before?: string) {
  const empty = { type: 'FeatureCollection', features: [] } as FC;
  if (!map.getSource(ASSETS_SOURCE)) {
    map.addSource(ASSETS_SOURCE, { type: 'geojson', data: empty });
    map.addLayer(
      {
        id: 'assets-fill',
        type: 'fill',
        source: ASSETS_SOURCE,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.15 },
      },
      before,
    );
    map.addLayer(
      {
        id: 'assets-line',
        type: 'line',
        source: ASSETS_SOURCE,
        filter: ['!=', ['geometry-type'], 'Point'],
        paint: { 'line-color': ['get', 'color'], 'line-width': 2 },
      },
      before,
    );
    map.addLayer(
      {
        id: 'assets-points',
        type: 'circle',
        source: ASSETS_SOURCE,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 16, 6],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1,
          'circle-opacity': 0.9,
        },
      },
      before,
    );
  }
  if (!map.getSource(HEAT_SOURCE)) {
    map.addSource(HEAT_SOURCE, { type: 'geojson', data: empty });
    map.addLayer(
      {
        id: 'ops-heat',
        type: 'heatmap',
        source: HEAT_SOURCE,
        paint: {
          'heatmap-weight': ['get', 'w'],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 8, 0.8, 15, 2],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 8, 8, 15, 28],
          'heatmap-opacity': 0.75,
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(33,102,172,0)',
            0.2,
            '#2c7bb6',
            0.45,
            '#abd9e9',
            0.7,
            '#fdae61',
            1,
            '#d7191c',
          ],
        },
      },
      before,
    );
  }
}

export function setOverlay(map: MapLibreMap, source: string, data: FC | null) {
  (map.getSource(source) as GeoJSONSource | undefined)?.setData(
    data ?? ({ type: 'FeatureCollection', features: [] } as FC),
  );
}
